import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import CheckIcon from '@assets/images/check-icon.svg?react'

import { Trade } from '@types'

import { Select, Option } from '@components/Select'

import { useTrades } from '@hooks/task-template/trades.hook'

import { TradeTag } from '@modules/_shared/components/TradeTag'

import { IconContainer, ItemContainer, ItemLabel, SelectedItemLabel, TagContainer } from './SelectTrade.styled'

interface Props {
  required?: boolean
  trade?: Trade
  onSelectTrade: (trade?: Trade) => void
  WrappedComponent?: () => JSX.Element
  disabledSearch?: boolean
  disabled?: boolean
  showLabel?: boolean
  dropdownAutoWidth?: boolean
}

export const SelectTrade = (props: Props): JSX.Element => {
  const {
    required,
    trade,
    disabledSearch = false,
    disabled = false,
    showLabel = true,
    dropdownAutoWidth = false,
    onSelectTrade,
    WrappedComponent,
  } = props

  const [search, setSearch] = useState<string>(trade?.label || '')

  useEffect(() => {
    setSearch(trade?.label || '')
  }, [trade, WrappedComponent])

  const { data } = useTrades()

  const selectedOption: Option | undefined = trade && {
    id: trade.key,
    label: trade.label,
    data: trade,
    ...(disabledSearch
      ? {
          Component: () => (
            <ItemContainer>
              <TagContainer>
                <TradeTag trade={trade} />
              </TagContainer>
              <SelectedItemLabel>{trade.label}</SelectedItemLabel>
            </ItemContainer>
          ),
        }
      : {}),
  }

  const options: Option[] = (data?.trades || [])
    .filter((_trade) => _trade.key == trade?.key || !_trade.disabled)
    .map((_trade) => ({
      id: _trade.key,
      label: _trade.label,
      data: _trade,
      Component: () => (
        <ItemContainer>
          <TagContainer>
            <TradeTag trade={_trade} />
          </TagContainer>

          <ItemLabel selected={_trade.key == trade?.key}>{_trade.label}</ItemLabel>

          {_trade.key == trade?.key && (
            <IconContainer>
              <CheckIcon />
            </IconContainer>
          )}
        </ItemContainer>
      ),
    }))

  const filteredOptions = WrappedComponent
    ? options
    : options.filter((option) => option.label.toLowerCase().indexOf((search || '').toLowerCase()) !== -1)

  const { t } = useTranslation()

  return (
    <Select
      disabledVerticalCorrection
      dropdownAutoWidth={dropdownAutoWidth}
      disabled={disabled}
      required={required}
      label={showLabel ? t('modules_project-settings_pages_add_task_template_label_task-trade') : undefined}
      placeholder={t('modules_project-settings_pages_add_task_template_placeholder_task-trade')}
      search={disabledSearch ? undefined : search}
      onSearch={disabledSearch ? undefined : setSearch}
      selected={selectedOption}
      options={disabledSearch ? options : filteredOptions}
      onClick={(option) => {
        if (option) {
          setSearch(option.label)

          onSelectTrade(option.data as Trade)
        } else {
          setSearch('')

          onSelectTrade()
        }
      }}
    />
  )
}
